// @flow strict

import type { ReduxDispatch, ResponseErrorType, SearchCriteria } from 'types';
import { createUntranslatedFeedback } from 'services/Feedback/actions';
import { report } from 'services/Errors/resources';

import {
    receivedCreateSuccess,
    receivedCreateFailure,
    receivedUpdateSuccess,
    receivedUpdateFailure,
    receivedDestroySuccess,
    receivedDestroyFailure,
    receivedQueryListSuccess,
    receivedQueryListFailure,
    receivedFetchListSuccess,
    receivedFetchListFailure,
    setIsCreatingStatus,
    setIsDeletingStatus,
    setIsUpdatingStatus,
    setIsQueryStatus,
    setIsFetchingStatus,
} from './actions';
import type { Oxime, OximeQueryType } from './types';
import { create, index, update, destroy, query } from './resources';

/**
 * Thunks
 *
 * Basic function: Validate response and dispatch action to save data/error to redux store
 */

/**
 * Create new oxime
 */
export const createOxime = (oxime: Oxime) => (dispatch: ReduxDispatch) => {
    dispatch(setIsCreatingStatus());
    create(oxime)
        .then((response: Oxime) => {
            dispatch(receivedCreateSuccess(response));
            dispatch(createUntranslatedFeedback('SUCCESS', 'feedback.success.createOximeSuccess'));
        })
        .catch((error: ResponseErrorType) => {
            report(error);
            dispatch(receivedCreateFailure(error));
            dispatch(createUntranslatedFeedback('ERROR', 'feedback.error.createOximeFailed'));
        });
};

export const fetchAllOximes = () => (dispatch: ReduxDispatch) => {
    dispatch(setIsFetchingStatus());
    index()
        .then((response: Array<Oxime>) => {
            dispatch(receivedFetchListSuccess(response));
        })
        .catch((error: ResponseErrorType) => {
            report(error);
            dispatch(receivedFetchListFailure(error));
            dispatch(createUntranslatedFeedback('ERROR', 'feedback.error.fetchAllOximesFailed'));
        });
};

/**
 * Query (search, pagination, etc) all oximes
 */
export const queryAllOximes = (searchCriteria: SearchCriteria, page: number, perPage: number) => (
    dispatch: ReduxDispatch
) => {
    dispatch(setIsQueryStatus());
    query(searchCriteria, page, perPage)
        .then((response: OximeQueryType) => {
            dispatch(receivedQueryListSuccess(response));
        })
        .catch((error: ResponseErrorType) => {
            report(error);
            dispatch(receivedQueryListFailure(error));
            dispatch(createUntranslatedFeedback('ERROR', 'feedback.error.queryAllOximesFailed'));
        });
};

/**
 * Update oxime
 */
export const updateOxime = (id: number, oxime: Oxime) => (dispatch: ReduxDispatch) => {
    dispatch(setIsUpdatingStatus());
    update(id, oxime)
        .then((response: Oxime) => {
            dispatch(receivedUpdateSuccess(response));
            dispatch(createUntranslatedFeedback('INFO', 'feedback.info.updateOxime'));
        })
        .catch((error: ResponseErrorType) => {
            report(error);
            dispatch(receivedUpdateFailure(error));
            dispatch(createUntranslatedFeedback('ERROR', 'feedback.error.updateOximeFailed'));
        });
};

/**
 * Destroy oxime
 */
export const destroyOxime = (id: number) => (dispatch: ReduxDispatch) => {
    dispatch(setIsDeletingStatus());
    destroy(id)
        .then((response: Oxime) => {
            dispatch(receivedDestroySuccess(response));
            dispatch(createUntranslatedFeedback('SUCCESS', 'feedback.success.destroyOximeSuccess'));
        })
        .catch((error: ResponseErrorType) => {
            report(error);
            dispatch(receivedDestroyFailure(error));
            dispatch(createUntranslatedFeedback('ERROR', 'feedback.error.destroyOximeFailed'));
        });
};
